import type { WorkbenchConfig } from "@/types";

export const WORKBENCH_CONFIG: Record<string, WorkbenchConfig> = {
  "trader-intelligence": {
    title: "Trader Intelligence",
    description: "Discover top traders, monitor activity, and manage trader watchlists.",
    actions: [
      {
        id: "trader-leaderboard",
        label: "Leaderboard",
        method: "GET",
        endpoint: "/api/traders/leaderboard?limit=20",
      },
      {
        id: "trader-discover",
        label: "Discover Traders",
        method: "POST",
        endpoint: "/api/traders/discover",
        defaultBody: {
          markets_limit: 10,
          min_volume: 10000,
        },
      },
      {
        id: "trader-watchlist",
        label: "Current Watchlist",
        method: "GET",
        endpoint: "/api/traders/watchlist",
      },
    ],
  },
  "news-center": {
    title: "News Center",
    description: "Search news pipelines and inspect both fresh and persisted coverage.",
    actions: [
      {
        id: "news-search",
        label: "Search Live News",
        method: "POST",
        endpoint: "/api/news/search",
        defaultBody: {
          query: "federal reserve",
          limit: 25,
        },
      },
      {
        id: "news-store-articles",
        label: "Stored Articles",
        method: "GET",
        endpoint: "/api/news-store/articles?limit=50",
      },
      {
        id: "news-store-sources",
        label: "Stored Sources",
        method: "GET",
        endpoint: "/api/news-store/sources",
      },
    ],
  },
  "resolution-center": {
    title: "Resolution Center",
    description: "Track pending and recently resolved markets for resolution risk.",
    actions: [
      {
        id: "resolution-pending",
        label: "Pending Resolutions",
        method: "GET",
        endpoint: "/api/resolution/pending",
      },
      {
        id: "resolution-recent",
        label: "Recent Resolutions",
        method: "GET",
        endpoint: "/api/resolution/recent",
      },
      {
        id: "resolution-sources",
        label: "Resolution Sources",
        method: "POST",
        endpoint: "/api/resolution/sources",
        defaultBody: {
          market_id: "fed-rates-july",
          question: "Will the Fed cut rates before July?",
          sources: [
            {
              url: "https://www.federalreserve.gov/monetarypolicy/fomccalendars.htm",
              name: "Federal Reserve FOMC",
              type: "official",
            },
          ],
        },
      },
    ],
  },
  "cross-market": {
    title: "Cross Market",
    description: "Compare equivalent markets across venues and surface arbitrage opportunities.",
    actions: [
      {
        id: "cross-compare",
        label: "Compare Markets",
        method: "GET",
        endpoint:
          "/api/cross-market/compare?question=Will%20BTC%20be%20above%20110k%20this%20year%3F",
      },
      {
        id: "cross-arb",
        label: "Arbitrage Opportunities",
        method: "GET",
        endpoint: "/api/cross-market/arbitrage?limit=20",
      },
      {
        id: "cross-search",
        label: "Market Search",
        method: "GET",
        endpoint: "/api/cross-market/search?q=election",
      },
    ],
  },
  "ops-pulse": {
    title: "Ops Pulse",
    description: "Check health, collector status, historical ingestion, and news pipelines.",
    actions: [
      {
        id: "ops-health",
        label: "Deep Health",
        method: "GET",
        endpoint: "/api/health/deep",
      },
      {
        id: "ops-collect-status",
        label: "Collector Status",
        method: "GET",
        endpoint: "/api/collect/status",
      },
      {
        id: "ops-historical",
        label: "Historical Stats",
        method: "GET",
        endpoint: "/api/historical/stats",
      },
      {
        id: "ops-news-store",
        label: "News Store Stats",
        method: "GET",
        endpoint: "/api/news-store/stats",
      },
    ],
  },
  "cost-center": {
    title: "Cost Center",
    description: "Inspect model usage, pricing analysis, and administrative cost controls.",
    actions: [
      {
        id: "cost-usage",
        label: "Usage Summary",
        method: "GET",
        endpoint: "/api/usage",
      },
      {
        id: "cost-analysis",
        label: "Cost Analysis",
        method: "GET",
        endpoint: "/api/costs/analysis?days=30",
      },
      {
        id: "cost-usage-breakdown",
        label: "Usage Breakdown",
        method: "GET",
        endpoint: "/api/costs/usage?days=30",
      },
      {
        id: "cost-reset",
        label: "Reset Usage Counters",
        method: "POST",
        endpoint: "/api/usage/reset",
      },
    ],
  },
  reports: {
    title: "Reports",
    description: "View consolidated prediction, arbitrage, and backtest reporting feeds.",
    actions: [
      {
        id: "reports-predictions",
        label: "Prediction Summary",
        method: "GET",
        endpoint: "/api/predictions/summary",
      },
      {
        id: "reports-arb",
        label: "Arbitrage Stats",
        method: "GET",
        endpoint: "/api/arbitrage/stats",
      },
      {
        id: "reports-backtests",
        label: "Backtest History",
        method: "GET",
        endpoint: "/api/backtest/history?limit=25",
      },
    ],
  },
  integrations: {
    title: "Integrations",
    description: "Inspect model provider availability and external integration status.",
    actions: [
      {
        id: "integrations-council",
        label: "Council Status",
        method: "GET",
        endpoint: "/api/council/status",
      },
      {
        id: "integrations-premium-models",
        label: "Premium Models",
        method: "GET",
        endpoint: "/api/premium/models",
      },
      {
        id: "integrations-ultra-providers",
        label: "Ultra Providers",
        method: "GET",
        endpoint: "/api/ultra/providers",
      },
    ],
  },
  "advanced-analytics": {
    title: "Advanced Analytics",
    description:
      "Run deeper model performance and trading analytics from prediction and portfolio systems.",
    actions: [
      {
        id: "advanced-prediction-compare",
        label: "Model Comparison",
        method: "GET",
        endpoint: "/api/predictions/compare",
      },
      {
        id: "advanced-calibration",
        label: "Calibration Curve",
        method: "GET",
        endpoint: "/api/predictions/calibration?model=consensus",
      },
      {
        id: "advanced-paper-portfolio",
        label: "Paper Portfolio",
        method: "GET",
        endpoint: "/api/paper/portfolio",
      },
    ],
  },
  "analysis-demo": {
    title: "Analysis Demo",
    description: "Sandbox classic analysis endpoint requests with structured payloads.",
    actions: [
      {
        id: "analysis-demo-run",
        label: "Run Analyze",
        method: "POST",
        endpoint: "/api/analyze",
        defaultBody: {
          market_id: "inflation-q4",
          question: "Will inflation be above 3.0% in Q4?",
          market_price: 0.41,
          rules: "Official BLS CPI release",
        },
      },
      {
        id: "analysis-demo-history",
        label: "Analysis History",
        method: "GET",
        endpoint: "/api/analyze/history?limit=50",
      },
    ],
  },
  manifold: {
    title: "Manifold",
    description: "Pull Manifold market feeds for cross-venue monitoring.",
    actions: [
      {
        id: "manifold-markets",
        label: "Manifold Markets",
        method: "GET",
        endpoint: "/api/manifold/markets?limit=20",
      },
    ],
  },
  predictit: {
    title: "PredictIt",
    description: "Inspect PredictIt market snapshots and arbitrage overlays.",
    actions: [
      {
        id: "predictit-markets",
        label: "PredictIt Markets",
        method: "GET",
        endpoint: "/api/predictit/markets",
      },
      {
        id: "predictit-arb",
        label: "Cross-Market Arbitrage",
        method: "GET",
        endpoint: "/api/cross-market/arbitrage?limit=20",
      },
    ],
  },
  "fred-economic": {
    title: "FRED Economic",
    description: "Pull macroeconomic time series from FRED integrations.",
    actions: [
      {
        id: "fred-fedfunds",
        label: "FEDFUNDS Series",
        method: "GET",
        endpoint: "/api/fred/series/FEDFUNDS",
      },
      {
        id: "fred-cpi",
        label: "CPIAUCSL Series",
        method: "GET",
        endpoint: "/api/fred/series/CPIAUCSL",
      },
    ],
  },
  "twitter-analysis": {
    title: "Twitter Analysis",
    description: "Search X/Twitter signals and sentiment streams for market intelligence.",
    actions: [
      {
        id: "twitter-search",
        label: "Search Twitter",
        method: "GET",
        endpoint: "/api/twitter/search?query=polymarket%20fed",
      },
    ],
  },
  "youtube-analysis": {
    title: "YouTube Analysis",
    description: "Fetch transcript and sentiment analysis for YouTube intelligence content.",
    actions: [
      {
        id: "youtube-transcribe",
        label: "Transcribe Video",
        method: "GET",
        endpoint:
          "/api/youtube/transcribe?url=https%3A%2F%2Fwww.youtube.com%2Fwatch%3Fv%3DdQw4w9WgXcQ",
      },
      {
        id: "youtube-analyze",
        label: "Analyze Video",
        method: "GET",
        endpoint:
          "/api/youtube/analyze?url=https%3A%2F%2Fwww.youtube.com%2Fwatch%3Fv%3DdQw4w9WgXcQ",
      },
    ],
  },
};
